import {inject, Injectable} from '@angular/core';
import * as amplitude from '@amplitude/analytics-browser';

import {environment} from "../../../environments/environment";
import {NavigationEnd, Router} from "@angular/router";
import {UserService} from "./user.service";
import {distinctUntilChanged, filter, map, merge, take} from "rxjs";
import {BotDetectionService} from "./bot-detection.service";
import {PlatformService} from "./platform.service";
import {platform} from "../utils";
import {AnalyticsService, AmplitudeEvent, PaymentEventProperties} from "./analytics.service";
import {TikTokTrackingService} from "./tiktok-tracking.service";
import {isNative} from "../utils";

const isBrowserEnvironment =
  typeof document !== 'undefined' &&
  typeof window !== 'undefined';
const amplitudeReturn: amplitude.Types.AmplitudeReturn<void> | null = isBrowserEnvironment
  ? amplitude.init(environment.amplitudeKey, {
      serverUrl: environment.amplitudeServerUrl,
      autocapture: {elementInteractions: true},
      logLevel: amplitude.Types.LogLevel.Warn,
      transport: 'beacon',
    })
  : null;

const platformMap: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  web: 'Web',
};

const mappedPlatform = platformMap[platform] ?? 'Unknown';

let nativeAppVersion: string | undefined;

const platformPlugin: amplitude.Types.EnrichmentPlugin = {
  name: 'PlatformPlugin',
  type: 'enrichment',
  setup: async () => {
    if (isNative) {
      try {
        const {App} = await import('@capacitor/app');
        const info = await App.getInfo();
        nativeAppVersion = info.version;
      } catch (_) {}
    }
  },

  execute: async (event) => {
    event.platform = mappedPlatform;
    if (nativeAppVersion) {
      event.app_version = nativeAppVersion;
    }
    return event;
  },
};

if (isBrowserEnvironment) {
  amplitude.add(platformPlugin);
}


@Injectable({
  providedIn: 'root'
})
export class BrowserAnalyticsService extends AnalyticsService {
  private readonly platformService = inject(PlatformService);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly botDetectionService = inject(BotDetectionService);
  private readonly tikTokTrackingService = inject(TikTokTrackingService);

  public async sendEvent(event: AmplitudeEvent, properties?: Record<string, string | number | undefined>) {
    if (this.platformService.isServer || !amplitudeReturn) {
      return;
    }

    // Fire pixel events immediately — do NOT gate them behind Amplitude awaits,
    // otherwise page navigations can prevent them from executing.
    if (environment.enableTrackingPixels) {
      const fbEventName = this.mapToFacebookEventName(event);
      if (fbEventName) {
        this.trackFacebookEvent(fbEventName);
      }

      this.tikTokTrackingService.trackEvent(event, properties);

      const googleEvent = this.mapToGoogleAdsEventName(event);
      if (googleEvent && environment.googleAdsId) {
        this.trackGoogleAdsEvent(googleEvent.eventId, googleEvent.value);
      }
    }

    try {
      await amplitudeReturn.promise;
      await amplitude.track(`[Homey] ${event}`, properties).promise;
      await amplitude.flush().promise;
    } catch (e) {
      console.error('Failed to send event', e);
    }

  }

  public mapToFacebookEventName(event: AmplitudeEvent): string | null {
    switch (event) {
      case 'signup-success':
        return 'CompleteRegistration';
      case 'payment-success':
        return 'Purchase';
      case 'payment-started':
        return 'InitiateCheckout';
      case 'benefits-page-viewed':
        return 'ViewContent';
      default:
        return null;
    }
  }

  public mapToGoogleAdsEventName(event: AmplitudeEvent): {
    eventId: string;
    value: number
  } | null {
    switch (event) {
      case 'signup-success':
        return {
          eventId: "vNWNCKKmgvcbEJmckfQ-",
          value: 2.0,
        }
      case 'payment-success':
        return {
          eventId: "gzAlCKL5gfcbEJmckfQ-",
          value: 20,
        };
      default:
        return null;
    }
  }

  public async setClickSource(source: string, url: string, variant?: string) {
    if (this.platformService.isServer || !amplitudeReturn) {
      return;
    }

    try {
      const eventProps: Record<string, string | undefined> = {
        click_source: source,
        url: url,
        click_variant: variant,
      };

      this.sendEvent('click-tracked', eventProps);
      await amplitudeReturn.promise;
      const identify = new amplitude.Identify().set('click_source', source);
      if (variant) {
        identify.set('click_variant', variant);
      }
      await amplitude.identify(identify).promise;
      await amplitude.flush().promise;
    } catch (e) {
      console.error('Failed to set click campaign', e);
    }
  }

  private trackGoogleAdsEvent(eventName: string, value: number) {
    if (this.platformService.isServer) {
      return;
    }

    const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
    if (typeof gtag !== 'function') {
      return;
    }

    try {
      gtag('event', 'conversion', {
        'send_to': `${environment.googleAdsId}/${eventName}`,
        'value': value,
        'currency': 'EUR',
        });


    } catch (err) {
      console.error('Google Ads track error', err);
    }
  }

  public buildPaymentEventProperties(
    base: {
      flow: PaymentEventProperties['flow'];
      checkoutLocation: PaymentEventProperties['checkoutLocation'];
      interval: PaymentEventProperties['interval'];
    }
  ): PaymentEventProperties {
    return {
      ...base,
      interval: base.interval ?? 'unknown',
    };
  }

  public async sendPaymentStarted(params: {
    flow: 'initial' | 'resubscribe',
    interval: 'monthly' | 'annual',
    checkoutLocation: 'default' | 'deferred',
    amount: number,
    couponId?: string,
  }) {
    try {
      const event = params.flow === 'resubscribe' ? 'resubscribe-payment-started' : 'payment-started';
      const properties = this.buildPaymentEventProperties({
        flow: params.flow,
        interval: params.interval,
        checkoutLocation: params.checkoutLocation,
      });

      await this.sendEvent(event, {...properties, amount: params.amount, couponId: params.couponId});
    } catch (err) {
      console.error('sendPaymentStarted analytics error', err);
    }
  }

  public async sendPaymentSuccess(params: {
    flow: 'initial' | 'resubscribe',
    checkoutLocation: 'default' | 'deferred',
    interval: PaymentEventProperties['interval'],
    amount?: number,
  }) {
    try {
      const properties = this.buildPaymentEventProperties({
        flow: params.flow,
        checkoutLocation: params.checkoutLocation,
        interval: params.interval,
      });
      await this.sendEvent('payment-success', {...properties, amount: params.amount});
    } catch (err) {
      console.error('sendPaymentSuccess analytics error', err);
    }
  }

  public getUTMData(): {utmSource: string, utmCampaign: string} {
    const prefix = environment.amplitudeKey.slice(0, 10);
    const cookieName = `AMP_MKTG_${prefix}`;
    let utmSource = 'unknown';
    let utmCampaign = 'unknown';
    if (!isBrowserEnvironment) {
      return {utmSource, utmCampaign};
    }

    try {
      // grab the cookie value
      const raw = document.cookie
        .split('; ')
        .find((c) => c.startsWith(`${cookieName}=`))
        ?.split('=')[1];

      if (raw) {
        // parse JSON safely
        const uriEncoded = atob(raw);                         // :contentReference[oaicite:1]{index=1}
        const data = JSON.parse(decodeURIComponent(uriEncoded));
        // pick out only what you need, falling back to defaults
        utmSource   = data.utm_source   ?? utmSource;
        utmCampaign = data.utm_campaign ?? utmCampaign;
      }
    } catch (err) {
      // swallow any errors—defaults will stay in place
      console.error('getUTMData error:', err);
    }

    return { utmSource, utmCampaign };
  }

  public initialize() {
    if (this.platformService.isServer || !amplitudeReturn) {
      return;
    }

    this.tikTokTrackingService.initialize();

    try {
      amplitudeReturn.promise.then(() => {
          this.userService.user$.pipe(
            map(user => user.status !== 'unauthorized' ? user.id : undefined),
            filter(id => !!id),
            distinctUntilChanged(),
          ).subscribe(id => {
            amplitude.setUserId(id);
          });

          amplitude.flush();

          this.setupSessionRecording();
        }
      )
    } catch (e) {
      console.error('Failed to initialize Amplitude', e);
    }
  }

  async setupSessionRecording() {
    if (environment.amplitude.sessionRecordingEnabled) {
      try {
        const bot = await this.botDetectionService.isBot();

        if (bot) {
          return;
        }

        const relevantPage = this.router.events.pipe(
          filter(event => event instanceof NavigationEnd),
          filter(event => event.url.includes('signup') || event.url.includes('login')),
        );

        const loggedInUser = this.userService.user$.pipe(
          filter(user => user.status !== 'unauthorized'),
        );

        merge(relevantPage, loggedInUser).pipe(
          take(1),
        ).subscribe(async () => {
          const { sessionReplayPlugin } = await import('@amplitude/plugin-session-replay-browser');
          amplitude.add(
            sessionReplayPlugin({
              sampleRate: 1,
            }),
          );
        });

      } catch (e) {
        console.error('Failed to setup session recording', e);
      }
    }
  }

  private trackFacebookEvent(name: string) {
    if (this.platformService.isServer) {
      return;
    }

    const fbq = (window as unknown as { fbq?: (...args: unknown[]) => void }).fbq;
    if (typeof fbq !== 'function') {
      return;
    }

    try {
      fbq('track', name);
    } catch (err) {
      console.error('Facebook pixel track error', err);
    }
  }
}
